/**
 * Map KMZ to Areas — edit dialog (AST-87, "revisitable"; AST-98 stored file)
 *
 * Opens with the project's saved elements in each section, grouped by their
 * KMZ folder path. AST-98: if the project has a stored KMZ/KML, the latest one
 * is loaded and parsed too, so every folder and placemark in it can be picked
 * again without re-uploading. Choosing a different file replaces that; it's
 * stored when the user saves (Cancel stores nothing and writes nothing).
 * Save sends the full mapping; the server inserts new elements and retires
 * removed ones.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { Upload as UploadIcon } from '@mui/icons-material';
import MapKmzToAreas, { emptySelections } from './MapKmzToAreas';
import { parseKmzFile } from '../../utils/kmzParser';
import { indexTree, elementsToSelections, buildSavePayload } from '../../utils/areaElements';
import {
  fetchAreaElements,
  saveAreaElements,
  areaErrorMessage,
  fetchKmzFiles,
  loadKmzFile,
  storeKmzFile,
  withSourceFile,
} from '../../services/areaElementsApi';
import { colorPalette } from '../../theme';

const formatSize = (bytes) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

/** Parse a File into { fileName, index }, or throw a readable error. */
async function indexFile(file) {
  const parsed = await parseKmzFile(file);
  const index = indexTree(parsed.tree, parsed.features);
  if (index.placemarks.size === 0) throw new Error('That file has no placemarks with coordinates.');
  return { fileName: parsed.fileName, index };
}

const MapAreasDialog = ({ open, projectId, onClose, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [selections, setSelections] = useState(emptySelections());
  // kmz: { fileName, index, stored?: { key, uploadId, size, storedAt }, file?: File }
  // `stored` = it came from the project's saved files; `file` = a new pick
  // that will be stored on Save.
  const [kmz, setKmz] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNotice(null);
    try {
      const [elementsRes, filesRes] = await Promise.all([
        fetchAreaElements(projectId),
        fetchKmzFiles(projectId).catch(() => null),
      ]);
      setSelections(elementsToSelections(elementsRes.items || []));

      const latest = filesRes?.latest;
      if (latest) {
        try {
          const file = await loadKmzFile(projectId, latest.uploadId);
          const indexed = await indexFile(file);
          setKmz({ ...indexed, stored: latest });
        } catch (fileErr) {
          // The mapping still works without it; say why the pickers are empty.
          setNotice(`The saved file "${latest.fileName}" couldn't be opened (${fileErr.message}). Upload it again to add elements.`);
        }
      }
    } catch (err) {
      setError(areaErrorMessage(err, "Couldn't load this project's areas. Try again."));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    if (!open) return;
    setKmz(null);
    setSelections(emptySelections());
    load();
  }, [open, load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError(null);
    setNotice(null);
    try {
      const indexed = await indexFile(file);
      setKmz({ ...indexed, file });
    } catch (err) {
      setError(`Couldn't read that file: ${err.message}`);
    }
  };

  const payload = useMemo(() => buildSavePayload(selections, kmz?.index || null), [selections, kmz]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      let sourceFileKey = kmz?.stored?.key || null;
      if (kmz?.file) {
        // AST-98: store the new file first, so the elements can point at it.
        try {
          const stored = await storeKmzFile(projectId, kmz.file);
          sourceFileKey = stored?.key || null;
        } catch (fileErr) {
          setError(areaErrorMessage(fileErr, "Couldn't store the KMZ file, so nothing was saved. Try again."));
          return;
        }
      }
      const res = await saveAreaElements(projectId, withSourceFile(payload, sourceFileKey));
      onSaved?.(res);
      onClose?.();
    } catch (err) {
      setError(areaErrorMessage(err, "Couldn't save the areas. Nothing was changed."));
    } finally {
      setSaving(false);
    }
  };

  let fileLine = 'Saved elements are shown below. Upload the KMZ/KML to add more.';
  if (kmz?.file) fileLine = `${kmz.fileName} (new, kept when you save)`;
  else if (kmz?.stored) fileLine = `${kmz.fileName} · saved ${formatSize(kmz.stored.size)}`;

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>Map KMZ to Areas</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {notice && <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress aria-label="Loading areas" /></Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Button component="label" variant="outlined" startIcon={<UploadIcon />} disabled={saving} sx={{ textTransform: 'none' }}>
                {kmz ? 'Choose a different file' : 'Upload KMZ/KML to add elements'}
                <input type="file" accept=".kmz,.kml" hidden onChange={handleFile} />
              </Button>
              <Typography variant="caption" color="text.secondary">{fileLine}</Typography>
            </Box>
            <MapKmzToAreas
              index={kmz?.index || null}
              selections={selections}
              onChange={setSelections}
              disabled={saving}
              skipped={kmz?.index?.skipped || 0}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving} sx={{ textTransform: 'none' }}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
          sx={{ backgroundColor: colorPalette.asseTracOrange, textTransform: 'none' }}
        >
          Save ({payload.length})
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapAreasDialog;
